// Settings — operator-facing configuration surface.
//
// Sections (top-down):
//   1. Profile  — read-only identity from auth.users
//   2. Connected services — daemon-heartbeat-derived integration status
//   3. Allowed threads (ADR-0009, Phase 1A only) — CRUD on `allowed_threads`
//   4. Excluded threads (N9) — CRUD on `excluded_threads`
//   5. Daily LLM budget — today's spend summary, links to /agents
//   6. Sign out — Supabase Auth signOut
//
// Inspired by the prototype's Settings.tsx layout, with two prototype
// sections explicitly dropped:
//   - "Auto-send" toggle per contact — violates N1 (Apr 21 rescope flipped
//     all channels to drafts-only or read-only). A toggle would be a lie.
//   - "Learning Loop sliders" (Conservative ↔ Proactive) — no agent reads
//     a proactiveness value. Pure decoration that doesn't shape behavior.
//
// Screen field audit:
//   - Profile email / id              ← supabase.auth.user (useAuth)
//   - Connected services tiles        ← daemon_heartbeat:* rows + classifyHeartbeat()
//   - Allowed threads list            ← allowed_threads (owner RLS, migration 0005)
//   - Excluded threads list           ← excluded_threads (owner RLS, migration 0005)
//   - Daily LLM budget value          ← app_settings.anthropic_tokens_used:<UTC-date>

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Bell,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Database,
  Download,
  EyeOff,
  ExternalLink,
  Laptop,
  Layers,
  LogOut,
  Shield,
  User as UserIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { readAllHeartbeats, classifyHeartbeat } from '@reverra/shared/heartbeat/read';
import GlassCard from '@/components/shared/GlassCard';
import PulseDot from '@/components/shared/PulseDot';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import ConnectedServicesCard from '@/components/settings/ConnectedServicesCard';
import DiscordNotificationsCard from '@/components/settings/DiscordNotificationsCard';
import ThreadFiltersCard from '@/components/settings/ThreadFiltersCard';
import { useAuth } from '@/lib/auth';
import { useOwnerProfile } from '@/hooks/useOwnerProfile';
import { supabase } from '@/lib/supabase';

// ============================================================================
// Daily LLM budget query
// ============================================================================

const TOKEN_USED_KEY_PREFIX = 'anthropic_tokens_used:';

function todayUtcKey(now: Date = new Date()): string {
  const yyyy = now.getUTCFullYear().toString().padStart(4, '0');
  const mm = (now.getUTCMonth() + 1).toString().padStart(2, '0');
  const dd = now.getUTCDate().toString().padStart(2, '0');
  return `${TOKEN_USED_KEY_PREFIX}${yyyy}-${mm}-${dd}`;
}

async function fetchTodayTokenUsage(): Promise<number | null> {
  const key = todayUtcKey();
  const { data, error } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', key)
    .maybeSingle();
  if (error) return null;
  const raw = data?.value ? Number.parseInt(data.value, 10) : null;
  return raw !== null && Number.isFinite(raw) && raw >= 0 ? raw : null;
}

// ============================================================================
// Page
// ============================================================================

const Settings = () => {
  const { user, signOut } = useAuth();

  // The Full Disk Access guide is an in-card expandable (not the browser's
  // native "Open System Settings?" prompt, which we can't restyle) so the
  // owner sees exactly which entry to enable before we bounce them to macOS.
  const [fdaGuideOpen, setFdaGuideOpen] = useState(false);
  const [copiedEntry, setCopiedEntry] = useState(false);

  // Tick `now` once a minute so the heartbeat freshness staleness
  // decays correctly without a reload.
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const heartbeatsQ = useQuery({
    queryKey: ['heartbeats'],
    queryFn: () => readAllHeartbeats(supabase),
    refetchInterval: 30_000,
    retry: 0,
  });

  const tokenUsageQ = useQuery({
    queryKey: ['settings', 'token_usage_today'],
    queryFn: fetchTodayTokenUsage,
    refetchInterval: 60_000,
    retry: 0,
  });

  // Stable refs to avoid the array identity changing every render
  // (which would invalidate ConnectedServicesCard's useMemo guards).
  const heartbeatRows = useMemo(() => heartbeatsQ.data ?? [], [heartbeatsQ.data]);

  const handleSignOut = async (): Promise<void> => {
    if (window.confirm('Sign out of Reverra Command Deck?')) {
      await signOut();
    }
  };

  const { name: ownerName, initials: ownerInitials } = useOwnerProfile();
  const tokenUsage = tokenUsageQ.data;

  // Live capture-engine status (for the "Reverra for Mac" card), derived from
  // the same daemon heartbeats. "engine running" = any daemon alive; iMessage
  // capture specifically needs the bridge_imessage reader (which needs Full
  // Disk Access on the Mac).
  const engineAlive = useMemo(
    () =>
      heartbeatRows.some((r) => r.payload && classifyHeartbeat(r.payload, now).status === 'alive'),
    [heartbeatRows, now],
  );
  const imessageAlive = useMemo(() => {
    const r = heartbeatRows.find((x) => x.daemon_name === 'bridge_imessage');
    return Boolean(r?.payload && classifyHeartbeat(r.payload, now).status === 'alive');
  }, [heartbeatRows, now]);
  // Set VITE_DESKTOP_APP_URL once the notarized .dmg is hosted; until then the
  // button shows a disabled "coming soon" state.
  const desktopAppUrl = import.meta.env.VITE_DESKTOP_APP_URL as string | undefined;

  return (
    <div className="space-y-10">
      <motion.header
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <h1 className="text-3xl font-serif font-semibold tracking-tight text-foreground">
          Settings
        </h1>
        <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
          Identity, integrations, and the privacy filters that govern what enters Cognitive Memory.
          Configuration that touches money or behavior lives elsewhere — Focus rules on /focus,
          commission rule snapshots on /commissions.
        </p>
      </motion.header>

      {/* ============================ */}
      {/* Profile                       */}
      {/* ============================ */}
      <section className="space-y-3">
        <SectionHeading icon={UserIcon} label="Profile" />
        <GlassCard hover={false}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-forest-soft flex items-center justify-center text-base font-medium text-forest shrink-0">
              {ownerInitials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground truncate">{ownerName}</p>
              <p className="text-[11px] text-muted-foreground truncate">{user?.email ?? '—'}</p>
            </div>
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
              owner
            </Badge>
          </div>
        </GlassCard>
      </section>

      {/* ============================ */}
      {/* Reverra for Mac (desktop app) */}
      {/* ============================ */}
      <section className="space-y-3">
        <SectionHeading icon={Laptop} label="Reverra for Mac" />
        <GlassCard hover={false}>
          <div className="flex items-start gap-4">
            <div className="neo-chip w-11 h-11 flex items-center justify-center shrink-0">
              <Laptop className="w-5 h-5 text-forest" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-medium text-foreground">Capture engine</p>
                <span className="inline-flex items-center gap-1 text-[11px]">
                  <PulseDot color={engineAlive ? 'green' : 'amber'} size="sm" />
                  <span className={engineAlive ? 'text-primary' : 'text-muted-foreground'}>
                    {engineAlive ? 'Running on this Mac' : 'Not running'}
                  </span>
                </span>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground max-w-xl">
                Reverra&apos;s background agents and the iMessage / WhatsApp bridge run on this Mac,
                so your data and messages never leave it. It&apos;s set up by the Reverra team and
                starts automatically on login — nothing to install here.
              </p>
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                iMessage / SMS capture:{' '}
                <span className={imessageAlive ? 'text-primary' : 'text-attention font-medium'}>
                  {imessageAlive ? 'active' : 'needs Full Disk Access'}
                </span>
                .
              </p>

              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setFdaGuideOpen((v) => !v)}
                  aria-expanded={fdaGuideOpen}
                >
                  <Shield className="w-3.5 h-3.5 mr-1.5" />
                  {imessageAlive ? 'Manage Full Disk Access' : 'Turn on iMessage capture'}
                  {fdaGuideOpen ? (
                    <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5 ml-1.5" />
                  )}
                </Button>
                {desktopAppUrl && (
                  <Button asChild size="sm">
                    <a href={desktopAppUrl} download>
                      <Download className="w-3.5 h-3.5 mr-1.5" />
                      Download Reverra for Mac
                    </a>
                  </Button>
                )}
              </div>

              {/* Inline Full Disk Access guide — neo-inset well. Carries the
                  real value (WHICH entry to enable) so it stands on its own
                  even before the OS bounce. */}
              {fdaGuideOpen && (
                <div className="neo-inset mt-3 p-4 text-xs">
                  <p className="text-foreground font-medium">
                    Give the capture engine Full Disk Access
                  </p>
                  <p className="mt-1 text-muted-foreground leading-relaxed">
                    macOS needs one permission to let Reverra read the iMessage &amp; SMS database
                    on this Mac. It stays entirely local.
                  </p>
                  <ol className="mt-3 space-y-2 list-decimal pl-4 text-muted-foreground leading-relaxed">
                    <li>
                      Open{' '}
                      <span className="text-foreground">
                        System Settings → Privacy &amp; Security → Full Disk Access
                      </span>{' '}
                      (the button below jumps you there).
                    </li>
                    <li>
                      In the list, find the entry named{' '}
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText('Reverra Capture').then(
                            () => {
                              setCopiedEntry(true);
                              setTimeout(() => setCopiedEntry(false), 1500);
                            },
                            () =>
                              toast.error('Could not copy — the entry is named “Reverra Capture”.'),
                          );
                        }}
                        className="neo-chip inline-flex items-center gap-1 px-1.5 py-0.5 text-forest font-medium align-baseline"
                      >
                        Reverra Capture
                        {copiedEntry ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      </button>{' '}
                      and switch its toggle <span className="text-foreground">on</span>. Don&apos;t
                      see it? Click the <span className="text-foreground font-medium">+</span>{' '}
                      button under the list and pick{' '}
                      <span className="text-foreground">Reverra Capture</span> from your
                      Applications folder.
                    </li>
                    <li>That&apos;s it — iMessage &amp; SMS start flowing within a minute.</li>
                  </ol>

                  <div className="neo-chip mt-3 p-2.5 flex gap-2 text-[11px] text-muted-foreground leading-relaxed">
                    <Shield className="w-3.5 h-3.5 text-attention shrink-0 mt-0.5" aria-hidden />
                    <span>
                      Older setups (from before the latest Reverra update) list this as{' '}
                      <span className="font-mono text-foreground">node</span> instead — re-running
                      the Reverra installer relabels it to{' '}
                      <span className="text-foreground">Reverra Capture</span> with our icon. If you
                      see more than one, enabling each is safe.
                    </span>
                  </div>

                  <div className="mt-3">
                    <Button asChild size="sm">
                      {/* macOS deep-link to the Full Disk Access pane. macOS shows
                          its own one-time "Open System Settings?" confirm — that
                          prompt is browser/OS chrome we can't restyle. */}
                      <a href="x-apple.systempreferences:com.apple.preference.security?Privacy_AllFiles">
                        <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                        Open System Settings
                      </a>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </GlassCard>
      </section>

      {/* ============================ */}
      {/* Connected services            */}
      {/* ============================ */}
      <section className="space-y-3">
        <SectionHeading icon={Layers} label="Connected services" />
        <p className="text-xs text-muted-foreground max-w-2xl">
          Status comes from each connector&apos;s daemon heartbeat. To connect or rotate
          credentials, the operator runs the relevant migration / OAuth step on the server — this
          surface reports the live state, it doesn&apos;t flip switches.
        </p>
        {heartbeatsQ.isLoading && (
          <p className="text-xs text-muted-foreground">Loading heartbeats…</p>
        )}
        {heartbeatsQ.isError && (
          <GlassCard hover={false} className="neo-alert">
            <p className="text-sm text-alert">
              Couldn&apos;t load heartbeat data: {(heartbeatsQ.error as Error).message}
            </p>
          </GlassCard>
        )}
        {heartbeatsQ.isSuccess && <ConnectedServicesCard rows={heartbeatRows} now={now} />}
      </section>

      {/* ============================ */}
      {/* Discord notifications         */}
      {/* ============================ */}
      <section className="space-y-3">
        <SectionHeading icon={Bell} label="Discord notifications" />
        <DiscordNotificationsCard />
      </section>

      {/* ============================ */}
      {/* Allowed threads (Phase 1A)    */}
      {/* ============================ */}
      <section className="space-y-3">
        <SectionHeading icon={Shield} label="Allowed threads (Phase 1A)" />
        <p className="text-xs text-muted-foreground max-w-2xl">
          ADR-0009: while <code className="font-mono">ALLOWLIST_MODE</code> is on (dev / staging),
          the bridge + parsers refuse to ingest any channel row whose identifier isn&apos;t on this
          list. Add the test contacts you want flowing into Cognitive Memory. At handover this list
          is decommissioned in favor of the excluded list below.
        </p>
        <ThreadFiltersCard variant="allowed" />
      </section>

      {/* ============================ */}
      {/* Excluded threads (N9)         */}
      {/* ============================ */}
      <section className="space-y-3">
        <SectionHeading icon={EyeOff} label="Excluded threads" />
        <p className="text-xs text-muted-foreground max-w-2xl">
          N9: privacy filter applied at INGEST, not at query. Anyone added here gets dropped at the
          bridge / webhook receiver before a memory row is ever written — no agent ever sees their
          content. Always-on; remains the operative filter post-handover.
        </p>
        <ThreadFiltersCard variant="excluded" />
      </section>

      {/* ============================ */}
      {/* Daily LLM budget               */}
      {/* ============================ */}
      <section className="space-y-3">
        <SectionHeading icon={Database} label="Daily LLM budget" />
        <GlassCard hover={false}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-2xl font-mono font-semibold text-foreground tabular-nums">
                {tokenUsage === null || tokenUsage === undefined
                  ? '—'
                  : tokenUsage.toLocaleString()}
                <span className="text-sm font-normal text-muted-foreground">
                  {tokenUsage === null || tokenUsage === undefined ? '' : ' tokens'}
                </span>
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {tokenUsage === null || tokenUsage === undefined
                  ? 'No usage recorded for today (UTC)'
                  : 'Used today (UTC) across every Anthropic-call worker'}
              </p>
            </div>
            <Link
              to="/agents"
              className="text-[11px] text-forest hover:text-forest/80 font-medium inline-flex items-center gap-1"
            >
              Open Agents →
            </Link>
          </div>
        </GlassCard>
      </section>

      {/* ============================ */}
      {/* Sign out                      */}
      {/* ============================ */}
      <section className="space-y-3">
        <SectionHeading icon={LogOut} label="Session" />
        <GlassCard hover={false}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm font-medium text-foreground">Sign out of this session</p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                The system keeps running on the server; signing out only ends your dashboard
                session.
              </p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={handleSignOut}>
              <LogOut className="w-4 h-4 mr-1.5" />
              Sign out
            </Button>
          </div>
        </GlassCard>
      </section>
    </div>
  );
};

function SectionHeading(props: { icon: typeof Shield; label: string }): JSX.Element {
  const Icon = props.icon;
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-forest" aria-hidden />
      <h2 className="text-sm font-medium text-foreground uppercase tracking-wide">{props.label}</h2>
      <div className="flex-1 h-px bg-border ml-1" />
    </div>
  );
}

export default Settings;
